import type { MeasureDateRange } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { pluralize } from "@entrilia/shared/utilities/stringHelper.ts";
import type { ConditionField, StatementLineConfiguration } from "../../../../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../../../../api/types/svTypes.ts";
import type { FormulaReferenceField } from "../../common/fields/custom-field/utilities/customFormulaParser.ts";
import { findFormulaCycle } from "./statementFormulaGraph.ts";
import { findPeriodAncestor, isValueLine } from "./statementLines.ts";
import type { LinePeriodOption } from "./statementOptions.ts";
import { getPeriodOptions, getRangeText } from "./statementOptions.ts";

export interface LinePeriodInfo {
  inheritedRange: MeasureDateRange;
  note: string;
}

export interface TotalPickerRow {
  guid: string;
  label: string;
  checked: boolean;
  cycleMessage?: string;
}

type PeriodLine = Pick<StatementLineConfiguration, "guid" | "lineType" | "level" | "dateRange">;

type PickerLine = Pick<StatementLineConfiguration, "guid" | "lineType" | "level" | "lineIds" | "formulaParameters">;

const OWN_PERIOD_NOTE = "Set on this line — its children inherit it unless they set their own.";
const REPORT_DEFAULT_PERIOD_NOTE = "Inherited from the report default.";
const EMPTY_TOTAL_NOTE = "Pick the lines this total adds up — they do not have to be its children.";

export const getLinePeriodInfo = <T extends PeriodLine>(
  lines: T[],
  line: T,
  defaultRange: MeasureDateRange,
  getLabel: (line: T) => string
): LinePeriodInfo => {
  const ancestor = findPeriodAncestor(lines, line.guid);
  const inheritedRange = ancestor?.dateRange ?? defaultRange;

  if (line.dateRange !== undefined) {
    return { inheritedRange, note: OWN_PERIOD_NOTE };
  }
  if (ancestor) {
    return { inheritedRange, note: `Inherited from “${getLabel(ancestor)}”.` };
  }
  return { inheritedRange, note: REPORT_DEFAULT_PERIOD_NOTE };
};

export const getLinePeriodOptions = (inheritedRange: MeasureDateRange): LinePeriodOption[] =>
  getPeriodOptions(`Inherit (${getRangeText(inheritedRange)})`);

const getOtherValueLines = <T extends PickerLine>(lines: T[], guid: string): T[] =>
  lines.filter((line) => line.guid !== guid && isValueLine(line));

const formatCyclePath = <T extends PickerLine>(lines: T[], cycle: string[], getLabel: (line: T) => string): string =>
  cycle
    .slice(1)
    .map((guid) => {
      const line = lines.find((candidate) => candidate.guid === guid);
      return `“${line ? getLabel(line) : guid}”`;
    })
    .join(" → ");

export const getTotalPickerRows = <T extends PickerLine>(
  lines: T[],
  total: T,
  getLabel: (line: T) => string
): TotalPickerRow[] =>
  getOtherValueLines(lines, total.guid).map((line) => {
    const checked = total.lineIds.includes(line.guid);
    const cycle = checked
      ? undefined
      : findFormulaCycle(lines, {
          guid: total.guid,
          lineType: "Total",
          lineIds: [...total.lineIds, line.guid],
          formulaParameters: total.formulaParameters,
        });

    return {
      guid: line.guid,
      label: getLabel(line),
      checked,
      cycleMessage: cycle && `This total would reference itself through ${formatCyclePath(lines, cycle, getLabel)}.`,
    };
  });

export const getFormulaReferableLines = <T extends PickerLine>(
  lines: T[],
  formulaGuid: string,
  getLabel: (line: T) => string
): FormulaReferenceField[] =>
  getOtherValueLines(lines, formulaGuid).map((line) => ({ guid: line.guid, caption: getLabel(line) }));

export const getFormulaCycleMessage = <T extends PickerLine>(
  lines: T[],
  formulaGuid: string,
  formulaParameters: string[],
  getLabel: (line: T) => string
): string | undefined => {
  const cycle = findFormulaCycle(lines, { guid: formulaGuid, lineType: "Formula", lineIds: [], formulaParameters });
  return cycle && `This formula would reference itself through ${formatCyclePath(lines, cycle, getLabel)}.`;
};

export const getTotalOfLinesNote = (pickedCount: number): string =>
  pickedCount === 0
    ? EMPTY_TOTAL_NOTE
    : `Sums ${pickedCount} ${pluralize("line", pickedCount)}, wherever they sit in the structure.`;

export const getLinkableLineConditions = (conditions: ConditionField[]): ConditionField[] =>
  conditions.filter((condition) => condition.meta.type !== ItemDataType.Date);
